import { useEffect, useState } from 'react';
import {
    Button,
    Card,
    Checkbox,
    Col,
    Divider,
    Form,
    Input,
    List,
    message,
    PageHeader,
    Row,
    Spin,
    Switch,
    Typography,
} from 'antd';
import { Helmet } from 'react-helmet';

import styles from './usereditpage.module.css';
import { useNavigate, useParams } from 'react-router-dom';
import UsersService from './UsersService';
import SurveysService from '../surveys/SurveysService';
import ClioService from '../clio/ClioService';

type RouteParams = {
    client: string;
    id?: string;
};

const UserEditPage = () => {
    const { client: clientSlug, id } = useParams() as RouteParams;
    const title = id ? 'Editar usuario' : 'Nuevo usuario';
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const returnPage = `/admin/clients/${clientSlug}/users`;
    const [sensibleDisabled, setSensibleDisabled] = useState(true);
    const [loading, setLoading] = useState(false);
    const [surveys, setSurveys] = useState<any[]>([]);
    const [selected, setSelected] = useState<number[]>([]);
    const [projects, setProjects] = useState<any[]>([]);
    const [selectedProjects, setSelectedProjects] = useState<number[]>([]);

    const saveChanges = (data: any) => {
        try {
            setLoading(true);

            if (id) {
                UsersService.update(id, data);
            } else {
                UsersService.create(data);
            }
            message.success('Se guardaron los cambios');
            navigate(returnPage);
        } catch (err) {
            setLoading(false);
            message.error((err as Error).message);
        }
    };

    useEffect(() => {
        const fetchUser = async () => {
            try {
                setLoading(true);
                //const data = await UsersService.get(id!);
                setLoading(false);
            } catch (err) {
                message.error((err as Error).message);
                setLoading(false);
            }
        };

        if (id) fetchUser();
    }, [id]);

    useEffect(() => {
        const fetchSurveys = async () => {
            try {
                const surveys = await SurveysService.getSurveys(
                    clientSlug,
                    '',
                    '',
                    1,
                    3000
                );
                setSurveys(surveys.results);
                const projects = await ClioService.getProjects(
                    clientSlug,
                    1,
                    3000
                );
                setProjects(projects.results);
            } catch (err) {
                message.error((err as Error).message);
            }
        };

        fetchSurveys();
    }, [clientSlug]);

    const onFieldsChanged = (changed: any, all: any) => {
        console.log(all);
        setSensibleDisabled(!all.is_admin);

        if (changed.hasOwnProperty('is_admin'))
            form.setFieldsValue({ sensible_data: changed.is_admin });
    };

    const selectSurvey = (itemId: number) => {
        let idx = selected.indexOf(itemId);
        if (idx === -1) {
            setSelected([...selected, itemId]);
        } else {
            setSelected(selected.filter((s) => s !== itemId));
        }
    };

    const selectProject = (itemId: number) => {
        let idx = selectedProjects.indexOf(itemId);
        if (idx === -1) {
            setSelectedProjects([...selected, itemId]);
        } else {
            setSelectedProjects(selectedProjects.filter((s) => s !== itemId));
        }
    };

    return (
        <>
            <Helmet>
                <title>{title}</title>
            </Helmet>
            <PageHeader
                onBack={() => navigate(returnPage)}
                title={title}
            ></PageHeader>
            <Spin spinning={loading}>
                <Form
                    className={styles.form}
                    layout="vertical"
                    form={form}
                    requiredMark="optional"
                    onValuesChange={onFieldsChanged}
                    onFinish={saveChanges}
                >
                    <Row gutter={[16, 16]} style={{ alignItems: 'center' }}>
                        <Col span={12}>
                            <Form.Item
                                label="Nombres"
                                name="first_name"
                                rules={[{ required: true }]}
                            >
                                <Input placeholder="Ej: José" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Apellidos" name="last_name">
                                <Input placeholder="Ej: Cárdenas" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Email"
                                name="email"
                                rules={[
                                    {
                                        type: 'email',
                                        message:
                                            'Debe ser una correo electrónico válido',
                                    },
                                ]}
                            >
                                <Input placeholder="Ej: jose.cardenas@miempresa.com" />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                label="¿Administrador?"
                                name="is_admin"
                                requiredMark={false}
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                label="¿Puede ver datos sensibles?"
                                name="sensible_data"
                                requiredMark={false}
                                valuePropName="checked"
                            >
                                <Switch disabled={sensibleDisabled} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Divider />
                    <Typography.Title level={4} style={{ marginBottom: 32 }}>
                        Permisos
                    </Typography.Title>

                    <Row gutter={[16, 16]}>
                        <Col span={12}>
                            <Typography.Title level={5}>
                                QuarteR
                            </Typography.Title>
                            <Form.Item
                                className={styles.formItemNoMargin}
                                name="permissions_quarter_all"
                                requiredMark={false}
                                valuePropName="checked"
                            >
                                <label>
                                    Acceso a todas las evaluaciones actuales y
                                    futuras <Switch />{' '}
                                </label>
                            </Form.Item>
                            <Card title="Evaluaciones" size="small">
                                {surveys.length === 0 && (
                                    <p>Sin acceso a evaluaciones</p>
                                )}
                                {surveys.length > 0 && (
                                    <List
                                        itemLayout="horizontal"
                                        dataSource={surveys}
                                        size="small"
                                        renderItem={(item) => (
                                            <List.Item>
                                                <List.Item.Meta
                                                    title={
                                                        <label>
                                                            <Checkbox
                                                                checked={
                                                                    selected.indexOf(
                                                                        item.id
                                                                    ) >= 0
                                                                }
                                                                onChange={() =>
                                                                    selectSurvey(
                                                                        item.id
                                                                    )
                                                                }
                                                            />{' '}
                                                            &nbsp;{item.title}
                                                        </label>
                                                    }
                                                />
                                            </List.Item>
                                        )}
                                    />
                                )}
                            </Card>
                        </Col>
                        <Col span={12}>
                            <Typography.Title level={5}>
                                Cliente oculto
                            </Typography.Title>
                            <Form.Item
                                className={styles.formItemNoMargin}
                                name="permissions_clio_all"
                                requiredMark={false}
                                valuePropName="checked"
                            >
                                <label>
                                    Acceso a todos los proyectos actuales y
                                    futuros <Switch />{' '}
                                </label>
                            </Form.Item>
                            <Card title="Proyectos" size="small">
                                {projects.length === 0 && (
                                    <p>Sin acceso a proyectos</p>
                                )}
                                {projects.length > 0 && (
                                    <List
                                        size="small"
                                        itemLayout="horizontal"
                                        dataSource={projects}
                                        renderItem={(item) => (
                                            <List.Item>
                                                <List.Item.Meta
                                                    title={
                                                        <label>
                                                            <Checkbox
                                                                checked={
                                                                    selectedProjects.indexOf(
                                                                        item.id
                                                                    ) >= 0
                                                                }
                                                                onChange={() =>
                                                                    selectProject(
                                                                        item.id
                                                                    )
                                                                }
                                                            />{' '}
                                                            &nbsp;{item.title}
                                                        </label>
                                                    }
                                                />
                                            </List.Item>
                                        )}
                                    />
                                )}
                            </Card>
                        </Col>
                    </Row>
                    <div className={styles.buttons}>
                        <Button href={returnPage}>Cancelar</Button>
                        <Button type="primary" htmlType="submit">
                            Guardar
                        </Button>
                    </div>
                </Form>
            </Spin>
        </>
    );
};

export default UserEditPage;
